import {
    QueryClient,
    type QueryKey,
    type UseBaseQueryOptions,
    type UseQueryResult,
    type UseSuspenseQueryResult,
    useQuery,
    useSuspenseQuery
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import isUndefined from "lodash-es/isUndefined"

import { coachRatingAdapter } from "@/3514/adapters"
import { ECoachRatingQueryKey } from "@/3514/config"
import { coachRatingFetcher } from "@/3514/fetchers"
import type { ICoachRatingDto, ICoachRatingModel, ICoachRatingTypeDto } from "@/3514/types"
import { hours, minutes, seconds } from "@/3514/utils/time.utils"

type _TCoachRatingQueryOptions = Pick<UseBaseQueryOptions, "staleTime" | "gcTime" | "enabled"> & {
    type: ICoachRatingTypeDto
    participantId: number
}

type TCoachRatingSuspenseQuery = UseSuspenseQueryResult<ICoachRatingModel, AxiosError>
type TCoachRatingQuery = UseQueryResult<ICoachRatingModel, AxiosError>

function useCoachRatingSuspenseQuery(options: _TCoachRatingQueryOptions): TCoachRatingSuspenseQuery {
    const _defaultOptions: Partial<_TCoachRatingQueryOptions> = {
        staleTime: undefined,
        gcTime: undefined,
        type: "post-module"
    }

    const actualOptions: _TCoachRatingQueryOptions = { ..._defaultOptions, ...options }

    return useSuspenseQuery<ICoachRatingDto, AxiosError, ICoachRatingModel, QueryKey>({
        queryKey: ECoachRatingQueryKey({ type: actualOptions.type, participantId: actualOptions.participantId }),
        queryFn: coachRatingFetcher,
        select: coachRatingAdapter,
        staleTime: isUndefined(actualOptions.staleTime) ? hours(1) : actualOptions.staleTime,
        gcTime: isUndefined(actualOptions.gcTime) ? minutes(20) : actualOptions.gcTime,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isError"]
    })
}

function useCoachRatingQuery(options: _TCoachRatingQueryOptions): TCoachRatingQuery {
    const _defaultOptions: Partial<_TCoachRatingQueryOptions> = {
        staleTime: undefined,
        gcTime: undefined,
        enabled: false,
        type: "post-module"
    }

    const actualOptions: _TCoachRatingQueryOptions = { ..._defaultOptions, ...options }

    return useQuery<ICoachRatingDto, AxiosError, ICoachRatingModel, QueryKey>({
        queryKey: ECoachRatingQueryKey({ type: actualOptions.type, participantId: actualOptions.participantId }),
        queryFn: coachRatingFetcher,
        select: coachRatingAdapter,
        staleTime: isUndefined(actualOptions.staleTime) ? hours(1) : actualOptions.staleTime,
        gcTime: isUndefined(actualOptions.gcTime) ? minutes(20) : actualOptions.gcTime,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isFetching", "isFetched", "isError"],
        enabled: actualOptions.enabled
    })
}

async function coachRatingQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { participantId }: { participantId: number }
): Promise<void> {
    await qc.prefetchQuery<ICoachRatingDto, AxiosError, ICoachRatingModel, QueryKey>({
        queryKey: ECoachRatingQueryKey({ type: "post-module", participantId }),
        queryFn: coachRatingFetcher,
        staleTime: hours(1),
        gcTime: minutes(20),
        networkMode: "online"
    })
}

export { useCoachRatingSuspenseQuery, useCoachRatingQuery }
export { coachRatingQueryPrefetcher }
export type { TCoachRatingSuspenseQuery, TCoachRatingQuery }
